import React, { useState } from "react";
import { FaWallet, FaShoppingCart, FaGavel, FaCircle } from "react-icons/fa";

const EscrowDashboard = ({ 
  escrows, 
  getStateText, 
  approvePayment, 
  initiateDispute, 
  sellerCancel, 
  resolveDispute, 
  EscrowStates,
  isLoading
}) => {
  const [activeTab, setActiveTab] = useState("buyer");
  
  const getStateClass = (state) => {
    switch(state) {
      case EscrowStates.PAID_TO_ESCROW:
        return "text-blue-400";
      case EscrowStates.DISPUTE:
        return "text-red-400";
      case EscrowStates.COMPLETE:
        return "text-green-400";
      case EscrowStates.REFUNDED:
        return "text-yellow-400";
      default:
        return "text-gray-400";
    }
  };

  const renderEscrowCards = (role) => {
    if (!escrows[role] || escrows[role].length === 0) {
      return (
        <div className="p-6 text-center bg-gray-800 bg-opacity-50 rounded-lg border border-gray-700">
          <div className="mb-4">
            <svg className="w-16 h-16 mx-auto text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h4 className="text-xl font-semibold text-gray-400">No Escrows Found</h4>
          <p className="mt-2 text-gray-500">You don't have any active escrows as {role}.</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-6">
        {escrows[role].map((escrow) => (
          <div key={escrow.id} className="p-6 bg-gray-800 rounded-xl shadow-xl border border-gray-700 hover:border-blue-500 transition-all duration-300">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center">
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-700 text-gray-300 mr-2">
                        ID: {escrow.id}
                      </span>
                      <span className={`flex items-center text-xs font-medium px-2 py-1 rounded-full bg-gray-700 ${getStateClass(escrow.currentState)}`}>
                        <FaCircle className="mr-1 h-2 w-2" />
                        {getStateText(escrow.currentState)}
                      </span>
                    </div>
                    <h3 className="mt-2 text-lg font-bold text-white">
                      {role === "buyer" ? "Paying" : role === "seller" ? "Receiving" : "Mediating"}
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">{escrow.amount}</div>
                    <div className="text-sm text-gray-400">
                      {escrow.token ? escrow.token.slice(-4) : "USDT"}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex">
                    <span className="w-24 text-sm text-gray-400">Buyer:</span>
                    <span className="text-sm text-white truncate">{escrow.buyer.slice(0, 6)}...{escrow.buyer.slice(-4)}</span>
                  </div>
                  <div className="flex">
                    <span className="w-24 text-sm text-gray-400">Seller:</span>
                    <span className="text-sm text-white truncate">{escrow.seller.slice(0, 6)}...{escrow.seller.slice(-4)}</span>
                  </div>
                  {escrow.arbiter && (
                    <div className="flex">
                      <span className="w-24 text-sm text-gray-400">Arbiter:</span>
                      <span className="text-sm text-white truncate">{escrow.arbiter.slice(0, 6)}...{escrow.arbiter.slice(-4)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full md:w-64 flex flex-col justify-end">
                {role === "buyer" && escrow.currentState === EscrowStates.PAID_TO_ESCROW && (
                  <div className="space-y-2">
                    <button
                      onClick={() => approvePayment(escrow.id)}
                      disabled={isLoading}
                      className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                    >
                      Approve Payment
                    </button>
                    <button
                      onClick={() => initiateDispute(escrow.id)}
                      disabled={isLoading}
                      className="w-full py-2 px-4 bg-yellow-600 hover:bg-yellow-700 text-white font-medium rounded-lg transition-colors"
                    >
                      Initiate Dispute
                    </button>
                  </div>
                )}
                
                {role === "seller" && escrow.currentState === EscrowStates.PAID_TO_ESCROW && (
                  <div className="space-y-2">
                    <button
                      onClick={() => initiateDispute(escrow.id)}
                      disabled={isLoading}
                      className="w-full py-2 px-4 bg-yellow-600 hover:bg-yellow-700 text-white font-medium rounded-lg transition-colors"
                    >
                      Initiate Dispute
                    </button>
                    <button
                      onClick={() => sellerCancel(escrow.id)}
                      disabled={isLoading}
                      className="w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                    >
                      Cancel/Refund
                    </button>
                  </div>
                )}
                
                {role === "arbiter" && escrow.currentState === EscrowStates.DISPUTE && (
                  <div className="space-y-2">
                    <button
                      onClick={() => resolveDispute(escrow.id, true)}
                      disabled={isLoading}
                      className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                    >
                      Release to Seller
                    </button>
                    <button
                      onClick={() => resolveDispute(escrow.id, false)}
                      disabled={isLoading}
                      className="w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                    >
                      Refund to Buyer
                    </button>
                  </div>
                )}
                
                {(escrow.currentState === EscrowStates.COMPLETE || 
                  escrow.currentState === EscrowStates.REFUNDED) && (
                  <div className="p-4 rounded-lg border border-gray-700 bg-gray-800 bg-opacity-50 text-center">
                    <div className={`text-2xl mb-2 ${escrow.currentState === EscrowStates.COMPLETE ? "text-green-400" : "text-yellow-400"}`}>
                      {escrow.currentState === EscrowStates.COMPLETE ? "✓" : "↩"}
                    </div>
                    <p className="text-sm text-gray-400">
                      {escrow.currentState === EscrowStates.COMPLETE 
                        ? "Transaction Completed" 
                        : "Funds Refunded"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mb-12">
      <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text">
        Your Escrow Transactions
      </h2>
      
      <div className="bg-gray-800 bg-opacity-50 rounded-xl overflow-hidden shadow-xl border border-gray-700">
      <div className="flex border-b border-gray-700">
  {/* Buyer tab */}
  <button
    className={`flex items-center px-6 py-4 text-sm font-medium focus:outline-none transition-colors duration-200 ${
      activeTab === "buyer" 
        ? "text-white border-b-2 border-blue-500 bg-gray-800" 
        : "text-gray-500 hover:text-gray-400"
    }`}
    style={{ 
      backgroundColor: activeTab === "buyer" ? "#1f2937" : "transparent",
      borderBottom: activeTab === "buyer" ? "2px solid #3b82f6" : "2px solid transparent" 
    }}
    onClick={() => setActiveTab("buyer")}
  >
    <FaWallet className="mr-2 text-gray-500" />
    As Buyer
    {escrows.buyer && escrows.buyer.length > 0 && (
      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-700 text-gray-500">
        {escrows.buyer.length}
      </span>
    )}
  </button>
  
  {/* Seller tab */}
  <button
    className={`flex items-center px-6 py-4 text-sm font-medium focus:outline-none transition-colors duration-200 ${
      activeTab === "seller" 
        ? "text-white border-b-2 border-green-500 bg-gray-800" 
        : "text-gray-500 hover:text-gray-400"
    }`}
    style={{ 
      backgroundColor: activeTab === "seller" ? "#1f2937" : "transparent",
      borderBottom: activeTab === "seller" ? "2px solid #10b981" : "2px solid transparent" 
    }}
    onClick={() => setActiveTab("seller")}
  >
    <FaShoppingCart className="mr-2 text-gray-500" />
    As Seller
    {escrows.seller && escrows.seller.length > 0 && (
      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-700 text-gray-500">
        {escrows.seller.length}
      </span>
    )}
  </button>
  
  {/* Arbiter tab */}
  <button
    className={`flex items-center px-6 py-4 text-sm font-medium focus:outline-none transition-colors duration-200 ${
      activeTab === "arbiter" 
        ? "text-white border-b-2 border-purple-500 bg-gray-800" 
        : "text-gray-500 hover:text-gray-400"
    }`}
    style={{ 
      backgroundColor: activeTab === "arbiter" ? "#1f2937" : "transparent",
      borderBottom: activeTab === "arbiter" ? "2px solid #8b5cf6" : "2px solid transparent" 
    }}
    onClick={() => setActiveTab("arbiter")}
  >
    <FaGavel className="mr-2 text-gray-500" />
    As Arbiter
    {escrows.arbiter && escrows.arbiter.length > 0 && (
      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-700 text-gray-500">
        {escrows.arbiter.length}
      </span>
    )}
  </button>
</div>
        
        <div className="p-6">
          {renderEscrowCards(activeTab)}
        </div>
      </div>
    </div>
  );
};

export default EscrowDashboard;